import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import type { IGetRowsParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';

import { CatalogWorkTypeItemsGql } from '../catalogTypesWorkQuery';

interface IUseCatalogTypesWork {
    filterValues: Record<string, string[]>;
    id: string | undefined;
}

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    return {
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (value.length) {
                acc = [
                    ...acc,
                    {
                        [key]: { in: value },
                    },
                ];
            }

            return acc;
        }, []),
    };
};

export const useCatalogTypesWorkData = ({ filterValues, id }: IUseCatalogTypesWork) => {
    const [catalogWorkTypesResponse, , , catalogWorkTypesMakeRequest] = useRequest({
        requestId: 'catalogWorkTypes',
        query: CatalogWorkTypeItemsGql,
        withEffect: false,
    });

    const variables = useMemo(
        () =>
            id
                ? {
                      catalogUniqueId: id,
                      where: getWhereVariables(filterValues),
                  }
                : undefined,
        [filterValues, id],
    );

    const changeOrderHandler = useCallback((params: IGetRowsParams) => {
        const { sortModel } = params;

        const { sort, colId } = sortModel?.[0] ?? {};

        if (colId && sort) {
            return [{ [colId]: sort.toUpperCase() }];
        }

        return undefined;
    }, []);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'catalogWorkTypes',
        requestFn: catalogWorkTypesMakeRequest,
        requestVariables: variables,
        customSortFn: changeOrderHandler,
    });

    useEffect(() => {
        if (catalogWorkTypesResponse?.catalogWorkTypeItems?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                catalogWorkTypesResponse.catalogWorkTypeItems?.items ?? [],
                catalogWorkTypesResponse.catalogWorkTypeItems?.totalCount,
            );
        }
    }, [
        catalogWorkTypesResponse?.catalogWorkTypeItems?.items,
        catalogWorkTypesResponse?.catalogWorkTypeItems?.totalCount,
        filterValues,
        getRowParams,
    ]);

    const totalCount = useMemo(
        () => catalogWorkTypesResponse?.catalogWorkTypeItems?.totalCount ?? 0,
        [catalogWorkTypesResponse?.catalogWorkTypeItems?.totalCount],
    );

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalCount, getRowParams?.current?.endRow]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
            changeOrderHandler,
        }),
        [changeOrderHandler, dataSource, fetchedCount, totalCount],
    );
};
